import React, { FC, useCallback, useEffect, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, styled, Theme } from '@material-ui/core';

import { Button, Message, Icons } from '../../../shared/components';
import GroupSelect from './GroupSelect';
import DeadlinePicker from './DeadlinePicker';
import TaskTypeSelect from './TaskTypeSelect';
import {
  Assignment,
  AssignmentTaskTypes,
  CreateAssignment,
} from '../../../../config/api/models/Assignment';
import { useFetch } from '../../../shared/hooks';
import apiRoutes from '../../../../config/api/routes';
import { ITrajectoryDetail } from '../../../../config/api/models/Trajectory';
import { LogActionTypes, useDataLogging } from '../../../shared/data-logging';
import { format } from 'date-fns';
import { useDispatch } from 'react-redux';
import { openSidePanel } from 'src/modules/shared/slices/sidePanel/sidePanelSlice';

type Props = {
  bookId: string;
  trajectory: ITrajectoryDetail;
};

const BACKEND_DATE_FORMAT = "yyyy-MM-dd'T'HH:mm:ss";

const Form = styled('form')<Theme>(({ theme }) => ({
  '> *': {
    marginTop: '3rem',
  },
  padding: theme.spacing(2),
}));

const FormGroup = styled('div')({
  marginBottom: '2rem',
});

const FormGroupTitle = styled(Typography)({
  marginBottom: '1rem',
});

const HorizontalSelectsWrapper = styled('div')({
  display: 'flex',
  alignItems: 'center',
});

const StyledButton = styled(Button)({
  marginTop: 0,
});

const NewAssignmentForm: FC<Props> = ({ bookId, trajectory }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch()

  const [errors, setErrors] = useState<string[]>([]);

  const [assignment, patchAssignment] = useReducer(
    (state: CreateAssignment, action: Partial<CreateAssignment>): CreateAssignment => ({
      ...state,
      ...action,
    }),
    {
      trajectoryId: trajectory.id,
      taskTypes: { full_trajectory: true, framework: false, theory: false, series: false },
      seriesIds: [],
      groupId: '',
      bookId: '',
    }
  );

  const [addAssignmentResponse, addAssignment] = useFetch<Assignment>(apiRoutes.addAssignment(), {
    method: 'POST',
  });

  const log = useDataLogging();

  const handleSubmit = useCallback(
    (event: any) => {
      event.preventDefault();

      const errors = [];
      if (!assignment.groupId) {
        errors.push('assignments.form.errors.assigneeRequired');
      }

      if (assignment.taskTypes.series && assignment.seriesIds.length === 0) {
        errors.push('assignments.form.errors.seriesRequired');
      }
      setErrors(errors);

      if (errors.length > 0) return;

      const taskTypesArray = (
        Object.keys(assignment.taskTypes) as (keyof AssignmentTaskTypes)[]
      ).filter((key) => !!assignment.taskTypes[key]);

      const body = {
        group_id: assignment.groupId,
        trajectory_id: assignment.trajectoryId,
        task_types: taskTypesArray,
        series_ids: assignment.seriesIds,
        expires_at: assignment.expiresAt && format(assignment.expiresAt, BACKEND_DATE_FORMAT),
        book_id: bookId,
      };
      addAssignment({ body });
    },
    [assignment, bookId, addAssignment]
  );

  useEffect(() => {
    if (addAssignmentResponse.success) {
      log(LogActionTypes.ASSIGNMENTS_CREATE, {
        bookId,
        trajectoryId: assignment?.trajectoryId,
        taskId: addAssignmentResponse?.data?.id,
        groupId: assignment?.groupId,
      });
    }
  }, [addAssignmentResponse, log, assignment, bookId]);

  useEffect(() => {
    if (!addAssignmentResponse.success) return;
      dispatch(openSidePanel(false));
  }, [addAssignmentResponse.success, dispatch]);

  const handleDeadlinePickerChange = (expiresAt: Date | undefined) => {
    patchAssignment({ expiresAt: expiresAt });
  };

  return (
    <Form onSubmit={handleSubmit}>
      <FormGroup>
        <FormGroupTitle variant="h3">
          {t('trajectory.sidebar.assignments.form.taskType')}
        </FormGroupTitle>
        <TaskTypeSelect
          trajectory={trajectory}
          value={{ taskTypes: assignment.taskTypes, seriesIds: assignment.seriesIds }}
          onChange={patchAssignment}
        />
      </FormGroup>

      <FormGroup>
        <FormGroupTitle variant="h3">
          {t('trajectory.sidebar.assignments.form.targetGroup')}
        </FormGroupTitle>
        <HorizontalSelectsWrapper>
          <GroupSelect
            trajectoryId={trajectory.id}
            bookId={bookId}
            value={assignment.groupId}
            onChange={(groupId) => patchAssignment({ groupId })}
          />
        </HorizontalSelectsWrapper>
      </FormGroup>

      <FormGroup>
        <FormGroupTitle variant="h3">
          {t('trajectory.sidebar.assignments.form.deadline')}
        </FormGroupTitle>
        <DeadlinePicker value={assignment.expiresAt} onChange={handleDeadlinePickerChange} />
      </FormGroup>

      <StyledButton type="submit" icon={Icons.ADD}>
        {t('trajectory.sidebar.assignments.form.createTask')}
      </StyledButton>
      {addAssignmentResponse.error && (
        <Message type="error">{addAssignmentResponse.reason}</Message>
      )}
      {errors.map((error, index) => (
        <Message type="error" key={index}>
          {t(error)}
        </Message>
      ))}
    </Form>
  );
};

export default NewAssignmentForm;
